import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
  Textarea,
  toast,
} from "@devhop/ui";
import { FormCategorySelect } from "@/modules/forms/presentation/ui/FormCategorySelect";
import { PresignedFileUpload } from "@/shared/ui/PresignedFileUpload";

type FormEditSettingsSectionProps = {
  formId: string;
  canUpdate: boolean;
  isSaving: boolean;
  title: string;
  description: string;
  categoryId: string | null;
  deadlineLocal: string;
  maxResponses: string;
  posterKey: string;
  posterReadUrl: string | null;
  onTitleChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onCategoryChange: (v: string | null) => void;
  onDeadlineChange: (v: string) => void;
  onMaxResponsesChange: (v: string) => void;
  onSave: () => void;
  onPosterUploaded: (key: string) => void;
  onPosterRemove: () => void;
};

export function FormEditSettingsSection({
  formId,
  canUpdate,
  isSaving,
  title,
  description,
  categoryId,
  deadlineLocal,
  maxResponses,
  posterKey,
  posterReadUrl,
  onTitleChange,
  onDescriptionChange,
  onCategoryChange,
  onDeadlineChange,
  onMaxResponsesChange,
  onSave,
  onPosterUploaded,
  onPosterRemove,
}: FormEditSettingsSectionProps) {
  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-lg">ຂໍ້ມູນ ແລະ ຂໍ້ຈຳກັດ</CardTitle>
        <CardDescription>
          ຫົວຂໍ້ ອະທິບາຍ ໝວດຫມູ່ ເສັ້ນກຳນົດ ແລະ ແບນເນີ public
        </CardDescription>
      </CardHeader>
      <Separator />
      <CardContent className="space-y-6">
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="flex flex-col gap-2 lg:col-span-2">
            <Label htmlFor="form-title">ຫົວຂໍ້ຟອມ</Label>
            <Input
              id="form-title"
              value={title}
              disabled={!canUpdate}
              placeholder="ຕົວຢ່າງ: ຟອມລົງທະບຽນທີມ"
              onChange={(e) => onTitleChange(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 lg:col-span-2">
            <Label htmlFor="form-desc">ອະທິບາຍ (ສະແດງໃນໜ້າ public)</Label>
            <Textarea
              id="form-desc"
              rows={3}
              disabled={!canUpdate}
              value={description}
              placeholder="ອະທິບາຍສັ້ນໆ ສຳລັບຜູ້ຕອບ"
              onChange={(e) => onDescriptionChange(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 lg:col-span-2">
            <FormCategorySelect
              value={categoryId}
              disabled={!canUpdate}
              onChange={onCategoryChange}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="form-deadline">ເສັ້ນກຳນົດປິດຮັບ</Label>
            <Input
              id="form-deadline"
              type="datetime-local"
              disabled={!canUpdate}
              value={deadlineLocal}
              onChange={(e) => onDeadlineChange(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">ວ່າງ = ບໍ່ກຳນົດເວລາປິດ</p>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="form-max">ຈຳນວນຄຳຕອບສູງສຸດ</Label>
            <Input
              id="form-max"
              type="number"
              inputMode="numeric"
              min={1}
              disabled={!canUpdate}
              value={maxResponses}
              onChange={(e) => onMaxResponsesChange(e.target.value)}
              placeholder="ບໍ່ກຳນົດ"
            />
          </div>
        </div>

        <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
          <div>
            <Label>ແບນເນີໜ້າ public</Label>
            <p className="mt-1 text-muted-foreground text-xs">
              ຮູບດ້ານເທິງຟອມ — JPEG, PNG, WebP ຫຼື GIF
            </p>
          </div>
          {posterReadUrl ? (
            <img
              src={posterReadUrl}
              alt=""
              className="max-h-44 w-full max-w-md rounded-md border bg-background object-contain"
            />
          ) : posterKey.trim().length > 0 ? (
            <p className="text-muted-foreground text-xs">
              ອັບໂຫຼດແລ້ວ — ກຳລັງລໍ preview URL
            </p>
          ) : (
            <div className="flex h-28 items-center justify-center rounded-md border border-dashed bg-background text-muted-foreground text-sm">
              ຍັງບໍ່ມີແບນເນີ
            </div>
          )}
          {canUpdate ? (
            <div className="flex flex-wrap gap-2">
              <PresignedFileUpload
                keyPrefix={`uploads/forms/posters/${formId}`}
                accept="image/jpeg,image/png,image/webp,image/gif"
                buttonLabel="ອັບໂຫຼດແບນເນີ"
                hint="JPEG, PNG, WebP ຫຼື GIF"
                onSuccess={onPosterUploaded}
                onError={() => toast.error("ອັບໂຫຼດແບນເນີບໍ່ສຳເລັດ")}
              />
              {posterKey.trim().length > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSaving}
                  onClick={onPosterRemove}
                >
                  ລຶບແບນເນີ
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end">
          <Button type="button" disabled={!canUpdate || isSaving} onClick={onSave}>
            {isSaving ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກການຕັ້ງຄ່າ"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
